import enviro from 'enviro';
import { createTracker } from 'usage-tracker-public';
import legoEvents from '../../events.yaml';
import { addIdToUrl, createUuid } from './createUuid';
import { getInitialTrackerProperties } from './getInitialTrackerProperties';
import { scrubAnonymousProperties } from './scrubAnonymousProperties';
import { UsageTrackingContainerLogger as LOG } from './UsageTrackingContainerLogger';
export const getPublicTracker = () => {
  return createTracker({
    events: legoEvents,
    properties: Object.assign({}, getInitialTrackerProperties(), {
      trackerType: 'xhr-request'
    }, scrubAnonymousProperties()),
    debug: () => !!enviro.debug('usage-tracker'),
    preserveTrackerProperties: true,
    lastKnownEventProperties: ['screen'],
    isExternalHost: false,
    isBeforeUnload: false
  });
};
/**
 * Exported only for testing.
 */

export const getAnonymousUsageTrackerContainer = () => ({
  TRACKER: getPublicTracker(),
  queue: [],
  isAnonymousTrackingEnabled: false,

  get() {
    return this.TRACKER;
  },

  getBeaconTracker() {
    return this.get().clone({
      isBeforeUnload: true,
      properties: {
        trackerType: 'beacon'
      }
    });
  },

  hasQueuedEvents() {
    return this.queue.length;
  },

  shouldQueue() {
    return !this.isAnonymousTrackingEnabled;
  },

  getHttpTracker() {
    return this.get();
  },

  queueEvent({
    eventName,
    properties = {},
    sendAsBeacon = false
  }) {
    LOG.queue(eventName, properties, sendAsBeacon);
    this.queue.push({
      eventName,
      properties,
      sendAsBeacon
    });
  },

  track(eventName, properties = {}, sendAsBeacon = false) {
    if (this.shouldQueue()) {
      this.queueEvent({
        eventName,
        properties,
        sendAsBeacon
      });
      return;
    }

    LOG.track(eventName, properties, sendAsBeacon);

    this._track({
      eventName,
      properties,
      sendAsBeacon
    });
  },

  drainQueue() {
    if (this.hasQueuedEvents()) {
      LOG.drainQueue(this.queue);
    }

    while (this.queue.length > 0) {
      const eventToSend = this.queue.shift();

      if (eventToSend) {
        const {
          eventName,
          properties,
          sendAsBeacon = false
        } = eventToSend;

        this._track({
          eventName,
          properties,
          sendAsBeacon
        });
      }
    }
  },

  enableNonIdentifiableTracking() {
    const uuid = createUuid();
    this.setProperties({
      deviceId: uuid
    });
    this.isAnonymousTrackingEnabled = true;
    this.drainQueue();
    this.track('interaction', {
      action: 'anonymous-tracking-enabled'
    });
  },

  setProperties(properties) {
    const propertiesToSet = Object.assign({}, properties, {
      hubId: null,
      email: null,
      hstc: null
    });
    this.get().setProperties(propertiesToSet);
  },

  set(trackerInstance) {
    this.TRACKER = trackerInstance;
  },

  enableTrackingByDeviceIdEmbedded(deviceId) {
    this.setProperties({
      hstc: null,
      deviceId
    });
    addIdToUrl(deviceId);
    this.isAnonymousTrackingEnabled = true;
    this.drainQueue();
    this.track('interaction', {
      action: 'anonymous-tracking-enabled-by-deviceId-consent'
    });
  },

  resetTracker() {
    this.TRACKER = getPublicTracker();
    this.isAnonymousTrackingEnabled = false;
  },

  /**
   * Internal track function that chooses the right tracker before
   * calling the track method.
   *
   * @param {String} eventName The event name.
   * @param {Object} properties The properties object.
   * @param {boolean} [sendAsBeacon=false]
   */
  _track({
    eventName,
    properties = {},
    sendAsBeacon = false
  }) {
    const tracker = sendAsBeacon ? this.getBeaconTracker() : this.getHttpTracker();
    tracker.track(eventName, properties);
  }

});
const AnonymousUsageTrackerContainer = getAnonymousUsageTrackerContainer();
export default AnonymousUsageTrackerContainer;